import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import '@google/model-viewer';
import * as THREE from 'three';
import { FBXLoader } from 'three/examples/jsm/loaders/FBXLoader';
import { PluginPreviewProps } from '../../types/PluginInterface';
import { usePaymentApiKeys } from '../../hooks/usePaymentApiKeys';
import { ALL_COUNTRIES } from '../../utils/countries';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import { PayPalScriptProvider } from '@paypal/react-paypal-js';
import PayPalCheckoutButtons from '../../components/PayPalCheckoutButtons';
import StripePaymentElementForm from '../../components/StripePaymentElementForm';
import AutoTranslateText from '../../components/AutoTranslateText';
import { EcommerceConfig, Product, CartItem, Order, ProductAttribute } from './types';
import { api } from '../../services/api';
import { loadGoogleMapsApi } from '../../utils/googleMapsLoader';

declare global {
  namespace JSX {
    interface IntrinsicElements {
      'model-viewer': any;
    }
  }
}

const isVideoUrl = (url: string) => /\.(mp4|webm|ogg|mov|avi|mkv)$/i.test(url.split('?')[0].split('#')[0]);
const isGlbUrl = (url: string) => /\.(glb|gltf)$/i.test(url.split('?')[0].split('#')[0]);
const isFbxUrl = (url: string) => /\.fbx$/i.test(url.split('?')[0].split('#')[0]);
const stripHtml = (html: string) => html.replace(/<[^>]*>/g, '').slice(0, 150);

const FbxViewer: React.FC<{ url: string; height?: number; className?: string; style?: React.CSSProperties }> = ({ url, height = 320, className, style }) => {
  const mountRef = useRef<HTMLDivElement>(null);
  const animRef = useRef<number | null>(null);
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
  const sceneRef = useRef<THREE.Scene | null>(null);
  const modelRef = useRef<THREE.Object3D | null>(null);

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;
    const width = mount.clientWidth || mount.parentElement?.clientWidth || 320;
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0xf5f5f5);
    sceneRef.current = scene;
    const camera = new THREE.PerspectiveCamera(45, width / height, 0.1, 1000);
    camera.position.set(2.5, 2.0, 3.2);
    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setSize(width, height);
    rendererRef.current = renderer;
    mount.appendChild(renderer.domElement);
    scene.add(new THREE.AmbientLight(0xffffff, 0.9));
    const dir = new THREE.DirectionalLight(0xffffff, 0.6);
    dir.position.set(5, 10, 7.5);
    scene.add(dir);
    const loader = new FBXLoader();
    loader.load(url, (obj) => {
      modelRef.current = obj;
      const box = new THREE.Box3().setFromObject(obj);
      const size = new THREE.Vector3();
      box.getSize(size);
      const maxDim = Math.max(size.x, size.y, size.z) || 1;
      const scale = 2 / maxDim;
      obj.scale.setScalar(scale);
      const center = new THREE.Vector3();
      box.getCenter(center);
      obj.position.sub(center.multiplyScalar(scale));
      scene.add(obj);
    }, undefined, (err) => console.error('Erreur FBX:', err));
    const onResize = () => {
      const w = mount.clientWidth || width;
      renderer.setSize(w, height);
      camera.aspect = w / height;
      camera.updateProjectionMatrix();
    };
    window.addEventListener('resize', onResize);
    const animate = () => {
      if (modelRef.current) modelRef.current.rotation.y += 0.01;
      renderer.render(scene, camera);
      animRef.current = requestAnimationFrame(animate);
    };
    animate();
    return () => {
      window.removeEventListener('resize', onResize);
      if (animRef.current) cancelAnimationFrame(animRef.current);
      if (rendererRef.current) {
        rendererRef.current.dispose();
        if (rendererRef.current.domElement.parentElement) {
          rendererRef.current.domElement.parentElement.removeChild(rendererRef.current.domElement);
        }
      }
      if (sceneRef.current && modelRef.current) sceneRef.current.remove(modelRef.current);
    };
  }, [url, height]);

  return <div ref={mountRef} className={className} style={{ width: '100%', height, ...style }} />;
};

const decodeHtml = (value: string) => {
  if (!value) return value;
  if (!value.includes('&lt;') && !value.includes('&gt;') && !value.includes('&amp;')) return value;
  if (typeof window === 'undefined') return value;
  const textarea = window.document.createElement('textarea');
  textarea.innerHTML = value;
  return textarea.value;
};

const getVideoEmbedUrl = (url: string) => {
  try {
    const trimmed = url.trim();
    if (!trimmed) return null;
    if (trimmed.includes('youtube.com')) {
      const parsed = new URL(trimmed);
      const id = parsed.searchParams.get('v');
      return id ? `https://www.youtube.com/embed/${id}` : null;
    }
    if (trimmed.includes('youtu.be/')) {
      const id = trimmed.split('youtu.be/')[1]?.split(/[?&]/)[0];
      return id ? `https://www.youtube.com/embed/${id}` : null;
    }
    if (trimmed.includes('vimeo.com/')) {
      const id = trimmed.split('vimeo.com/')[1]?.split(/[?&]/)[0];
      return id ? `https://player.vimeo.com/video/${id}` : null;
    }
    return null;
  } catch { return null; }
};

// ─── Styles constants ────────────────────────────────────────────────────────

const RADIUS = { sm: 8, md: 12, lg: 16, xl: 20, pill: 50 } as const;

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '10px 14px',
  borderRadius: RADIUS.md,
  border: '1.5px solid #e5e7eb',
  fontSize: 14,
  outline: 'none',
  boxSizing: 'border-box',
  background: '#fff',
};

// ─── Component ───────────────────────────────────────────────────────────────

const EcommerceShopRendererComponent: React.FC<PluginPreviewProps> = ({ config }) => {
  const cfg = config as EcommerceConfig;

  // ── Debug ──
  const renderCountRef = useRef(0);
  const prevConfigRef = useRef<any>(null);
  useEffect(() => {
    if (cfg.debugRenderCount) {
      renderCountRef.current += 1;
      console.log(`[EcommerceShopRenderer] render #${renderCountRef.current}`);
    }
  });
  useEffect(() => {
    if (!cfg.debugRenderCount) return;
    if (prevConfigRef.current) {
      const changedKeys: string[] = [];
      Object.keys(cfg || {}).forEach((key) => {
        if (prevConfigRef.current?.[key] !== (cfg as any)[key]) changedKeys.push(key);
      });
      if (changedKeys.length > 0) console.log('[EcommerceShopRenderer] config changed keys', changedKeys);
    }
    prevConfigRef.current = config;
  });
  useEffect(() => {
    if (!cfg.debugRenderCount) return;
    console.log('[EcommerceShopRenderer] mount', { id: cfg.id, ownerId: (cfg as any).ownerId || (cfg as any).userId });
    return () => { console.log('[EcommerceShopRenderer] unmount', { id: cfg.id }); };
  }, []);

  // ── Cart & UI state ──
  const [cart, setCart] = useState<CartItem[]>([]);
  const [showCart, setShowCart] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(cfg.defaultCategory || 'all');
  const defaultCountryLabel = ALL_COUNTRIES.find(c => c.code === (cfg.allowedCountries?.[0] || 'FR'))?.label || 'France';
  const [customerInfo, setCustomerInfo] = useState({ name: '', email: '', address: '', city: '', postalCode: '', country: defaultCountryLabel });
  const [billingInfo, setBillingInfo] = useState({ address: '', city: '', postalCode: '', country: defaultCountryLabel });
  const [useDifferentBilling, setUseDifferentBilling] = useState(false);
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const [availablePaymentMethods, setAvailablePaymentMethods] = useState<string[]>([]);
  const [paymentMethod, setPaymentMethod] = useState<string>('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [stripeBusy, setStripeBusy] = useState(false);
  const [stripeClientSecret, setStripeClientSecret] = useState<string | null>(null);
  const [pendingOrder, setPendingOrder] = useState<{ order: Order; productListHTML: string } | null>(null);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showRgpdModal, setShowRgpdModal] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [deliveryMethod, setDeliveryMethod] = useState<'shipping' | 'click_and_collect'>(
    cfg.deliveryType === 'click_and_collect' ? 'click_and_collect' : 'shipping'
  );
  const { stripePublicKey, paypalClientId } = usePaymentApiKeys();
  const [merchantKeys, setMerchantKeys] = useState<{ stripePublicKey?: string | null; paypalClientId?: string | null }>({});
  const [resolvedOwnerId, setResolvedOwnerId] = useState(() => {
    return cfg.ownerId || (cfg as any).userId || (cfg as any).createdBy || (cfg as any).created_by || (cfg as any).authorId || (cfg as any).user?.id || (cfg as any).user?.userId;
  });
  const [publicMapsKey, setPublicMapsKey] = useState<string | null>(null);
  const [activeImageIndex, setActiveImageIndex] = useState<Record<string, number>>({});

  // ── New modern state ──
  const [viewMode, setViewMode] = useState<'grid' | 'list' | 'carousel'>(cfg.displayMode || 'grid');
  const [sortBy, setSortBy] = useState<'default' | 'price_asc' | 'price_desc' | 'rating' | 'name'>('default');
  const [wishlist, setWishlist] = useState<string[]>([]);
  const [quickViewProduct, setQuickViewProduct] = useState<Product | null>(null);
  const [checkoutStep, setCheckoutStep] = useState<1 | 2 | 3>(1);
  const [hoveredCard, setHoveredCard] = useState<string | null>(null);
  // selectedAttributes per product being viewed (productId → {attrId → optionId})
  const [productAttrs, setProductAttrs] = useState<Record<string, Record<string, string>>>({});

  // ── Responsive breakpoints ──
  const [windowWidth, setWindowWidth] = useState(() => (typeof window !== 'undefined' ? window.innerWidth : 1024));
  useEffect(() => {
    const handler = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  const isMobile = windowWidth < 640;
  const isTablet = windowWidth < 900;

  // ── Carousel touch/swipe support ──
  const touchStartX = useRef<number | null>(null);
  const handleCarouselTouchStart = (e: React.TouchEvent) => { touchStartX.current = e.touches[0].clientX; };
  const handleCarouselTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(dx) > 50) {
      if (dx < 0) setCurrentProductIndex(p => (p < sortedProducts.length - 1 ? p + 1 : 0));
      else setCurrentProductIndex(p => (p > 0 ? p - 1 : sortedProducts.length - 1));
    }
    touchStartX.current = null;
  };

  // Carousel index (used only in carousel mode)
  const [currentProductIndex, setCurrentProductIndex] = useState(0);

  // Image modal
  const [imageModal, setImageModal] = useState<{ productId: string; images: string[]; index: number } | null>(null);
  const [zoom, setZoom] = useState(1);
  const modalMedia = imageModal ? imageModal.images[imageModal.index] : null;
  const modalEmbedUrl = modalMedia ? getVideoEmbedUrl(modalMedia) : null;
  const modalIsVideo = modalMedia ? isVideoUrl(modalMedia) : false;
  const modalIsGlb = modalMedia ? isGlbUrl(modalMedia) : false;
  const modalIsFbx = modalMedia ? isFbxUrl(modalMedia) : false;
  const modalIsZoomable = !!modalMedia && !modalEmbedUrl && !modalIsVideo && !modalIsGlb && !modalIsFbx;

  // ── Cart storage ──
  const cartStorageKeyRef = useRef<string | null>(null);
  const cartUiKeyRef = useRef<string | null>(null);
  const cartStorageRef = useRef<Storage | null>(null);
  if (!cartStorageKeyRef.current) {
    const keyBase = cfg.id || cfg.shopName || cfg.title || 'default';
    const storedUser = localStorage.getItem('user');
    let userId: string | null = null;
    try { if (storedUser) { const ud = JSON.parse(storedUser); userId = String(ud?.id || ud?.userId || ''); } } catch { userId = null; }
    const isLoggedIn = !!userId;
    let sessionId = sessionStorage.getItem('ecom_cart_session_id');
    if (!sessionId) { sessionId = `s_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`; sessionStorage.setItem('ecom_cart_session_id', sessionId); }
    const scope = isLoggedIn ? `user_${userId}` : `visitor_${sessionId}`;
    cartStorageRef.current = isLoggedIn ? localStorage : sessionStorage;
    cartStorageKeyRef.current = `ecom_cart_${keyBase}_${scope}`;
    cartUiKeyRef.current = `ecom_cart_ui_${keyBase}_${scope}`;
  }
  const cartStorageKey = cartStorageKeyRef.current;
  const cartUiKey = cartUiKeyRef.current;
  const checkoutUiKey = cartUiKey ? `${cartUiKey}_checkout` : null;
  const cartStorage = cartStorageRef.current;
  const pendingOrderKey = cartStorageKey ? `${cartStorageKey}_pending_order` : null;

  // ── Effects ──
  useEffect(() => {
    if (!cartStorageKey || !cartUiKey || !cartStorage) return;
    try {
      const raw = cartStorage.getItem(cartStorageKey);
      if (raw) { const parsed = JSON.parse(raw); if (Array.isArray(parsed)) setCart(parsed); }
      const uiRaw = cartStorage.getItem(cartUiKey);
      if (uiRaw) { const uiState = JSON.parse(uiRaw); if (typeof uiState?.showCart === 'boolean') setShowCart(uiState.showCart); }
      const checkoutRaw = checkoutUiKey ? cartStorage.getItem(checkoutUiKey) : null;
      if (checkoutRaw) { const cs = JSON.parse(checkoutRaw); if (typeof cs?.showCheckout === 'boolean') setShowCheckout(cs.showCheckout); }
    } catch { /* ignore */ }
  }, [cartStorageKey, cartUiKey, checkoutUiKey, cartStorage]);

  useEffect(() => {
    if (!cartStorageKey || !cartStorage) return;
    try { cartStorage.setItem(cartStorageKey, JSON.stringify(cart)); } catch { /* ignore */ }
  }, [cart, cartStorageKey, cartStorage]);

  useEffect(() => {
    if (stripeClientSecret) { setStripeClientSecret(null); setPendingOrder(null); }
  }, [cart]);

  useEffect(() => {
    if (!cartUiKey || !cartStorage) return;
    try { cartStorage.setItem(cartUiKey, JSON.stringify({ showCart })); } catch { /* ignore */ }
  }, [showCart, cartUiKey, cartStorage]);

  useEffect(() => {
    if (!checkoutUiKey || !cartStorage) return;
    try { cartStorage.setItem(checkoutUiKey, JSON.stringify({ showCheckout })); } catch { /* ignore */ }
  }, [showCheckout, checkoutUiKey, cartStorage]);

  useEffect(() => {
    if (paymentMethod !== 'stripe' && stripeClientSecret) { setStripeClientSecret(null); setPendingOrder(null); }
  }, [paymentMethod, stripeClientSecret]);

  useEffect(() => {
    const c = config as EcommerceConfig;
    setResolvedOwnerId(c.ownerId || (c as any).userId || (c as any).createdBy || (c as any).created_by || (c as any).authorId || (c as any).user?.id || (c as any).user?.userId);
  }, [config]);

  useEffect(() => {
    let cancelled = false;
    const fetchKeys = async () => {
      if (!resolvedOwnerId) { setMerchantKeys({}); return; }
      try {
        const keys = (await api.get(`/payment-keys/public/${resolvedOwnerId}`)) as any;
        if (cancelled) return;
        setMerchantKeys({ stripePublicKey: keys?.stripePublicKey || null, paypalClientId: keys?.paypalClientId || null });
      } catch { if (!cancelled) setMerchantKeys({}); }
    };
    fetchKeys();
    return () => { cancelled = true; };
  }, [resolvedOwnerId]);

  useEffect(() => {
    let cancelled = false;
    const fetchMapsKey = async () => {
      if (!resolvedOwnerId) { setPublicMapsKey(null); return; }
      try {
        const resp: any = await api.get(`/apiKeys/public/user/${resolvedOwnerId}/maps`);
        if (!cancelled) setPublicMapsKey(resp?.data?.keyValue || null);
      } catch { if (!cancelled) setPublicMapsKey(null); }
    };
    fetchMapsKey();
    return () => { cancelled = true; };
  }, [resolvedOwnerId]);

  useEffect(() => {
    setCurrentProductIndex(0);
  }, [searchTerm, selectedCategory]);

  const effectiveStripeKey = merchantKeys.stripePublicKey || stripePublicKey;
  const effectivePaypalKey = merchantKeys.paypalClientId || paypalClientId;
  const effectiveMapsKey = publicMapsKey;
  const stableStripeKeyRef = useRef<string | null>(null);
  if (!stableStripeKeyRef.current && effectiveStripeKey) stableStripeKeyRef.current = effectiveStripeKey;
  const stableStripeKey = stableStripeKeyRef.current;

  useEffect(() => {
    const methods: string[] = [];
    if (cfg.paymentMethods?.stripe && effectiveStripeKey) methods.push('stripe');
    if (cfg.paymentMethods?.paypal && effectivePaypalKey) methods.push('paypal');
    if (cfg.paymentMethods?.cash) methods.push('cash');
    setAvailablePaymentMethods(methods);
    if (methods.length > 0 && !paymentMethod) setPaymentMethod(methods[0]);
  }, [cfg.paymentMethods, effectiveStripeKey, effectivePaypalKey, paymentMethod]);

  // Click & Collect map
  useEffect(() => {
    if (deliveryMethod !== 'click_and_collect') return;
    if (!effectiveMapsKey || !cfg.clickAndCollect?.address) return;
    let cancelled = false;
    loadGoogleMapsApi(effectiveMapsKey).then(() => {
      if (cancelled) return;
      if (!window.google || !window.google.maps) return;
      const mapElement = document.getElementById('click-collect-map');
      if (mapElement && !(mapElement as any)._mapInitialized) {
        (mapElement as any)._mapInitialized = true;
        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode({ address: cfg.clickAndCollect!.address }, (results: google.maps.GeocoderResult[] | null, status: google.maps.GeocoderStatus) => {
          if (status === 'OK' && results) {
            const map = new window.google.maps.Map(mapElement, { center: results[0].geometry.location, zoom: 15 });
            new window.google.maps.Marker({ map, position: results[0].geometry.location });
          }
        });
      }
    }).catch((err) => console.error('Erreur chargement Google Maps API:', err));
    return () => { cancelled = true; };
  }, [deliveryMethod, effectiveMapsKey, cfg.clickAndCollect?.address]);

  // Image modal keyboard
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { setImageModal(null); setQuickViewProduct(null); }
      if (e.key === 'ArrowRight' && imageModal) setImageModal(m => m ? { ...m, index: (m.index + 1) % m.images.length } : m);
      if (e.key === 'ArrowLeft' && imageModal) setImageModal(m => m ? { ...m, index: (m.index - 1 + m.images.length) % m.images.length } : m);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [imageModal]);

  // Lock scroll
  useEffect(() => {
    const anyModal = imageModal || quickViewProduct || showCheckout || showCart;
    if (anyModal) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = prev; };
    }
  }, [imageModal, quickViewProduct, showCheckout, showCart]);

  // ── Stripe ──
  const stripePromise = useMemo(() => (stableStripeKey ? loadStripe(stableStripeKey) : null), [stableStripeKey]);
  const stripeElementsOptions = useMemo(() => {
    if (!stripeClientSecret) return undefined;
    return { clientSecret: stripeClientSecret, appearance: { theme: 'stripe' as const } };
  }, [stripeClientSecret]);

  // ── Helpers ──
  const getProductImages = (p: Product): string[] => {
    if (p.images && p.images.length) return p.images;
    return p.image ? [p.image] : [];
  };

  const openImageModal = (product: Product, index: number, images: string[]) => {
    setZoom(1);
    setImageModal({ productId: product.id, images, index });
  };

  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    e.currentTarget.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iI2U5ZWNlZiIvPjx0ZXh0IHRleHQtYW5jaG9yPSJtaWRkbGUiIHg9IjEwMCIgeT0iMTAwIiBmaWxsPSIjNmM3NTdkIiBmb250LXNpemU9IjE4IiBmb250LWZhbWlseT0iQXJpYWwiPkltYWdlPC90ZXh0Pjwvc3ZnPg==';
  };

  // ── Cart logic ──
  const getCartItemKey = (item: CartItem): string => {
    const attrs = item.selectedAttributes || {};
    const sortedAttrs = Object.keys(attrs).sort().map(k => `${k}:${attrs[k]}`).join('|');
    return `${item.product.id}__${sortedAttrs}`;
  };
  const getEffectivePrice = (product: Product, selectedAttrs?: Record<string, string>): number => {
    let price = product.salePrice ?? product.price;
    if (!selectedAttrs || !product.attributes?.length) return price;
    product.attributes.forEach(attr => {
      const optId = selectedAttrs[attr.id];
      if (optId) {
        const opt = attr.options.find(o => o.id === optId);
        if (opt?.priceAdjustment) price += opt.priceAdjustment;
      }
    });
    return Math.max(0, price);
  };
  const hasRequiredAttrs = (product: Product, selectedAttrs: Record<string, string>): boolean => {
    if (!product.attributes?.length) return true;
    return product.attributes.filter(a => a.required).every(a => !!selectedAttrs[a.id]);
  };
  const addToCart = (product: Product, selectedAttributes?: Record<string, string>) => {
    if (product.stock <= 0) return;
    const attrs = selectedAttributes || {};
    const newKey = `${product.id}__${Object.keys(attrs).sort().map(k => `${k}:${attrs[k]}`).join('|')}`;
    const existing = cart.find(i => getCartItemKey(i) === newKey);
    if (existing) {
      setCart(cart.map(i => getCartItemKey(i) === newKey ? { ...i, quantity: Math.min(i.quantity + 1, product.stock) } : i));
    } else {
      setCart([...cart, { product, quantity: 1, selectedAttributes: Object.keys(attrs).length ? attrs : undefined }]);
    }
  };
  const removeFromCart = (key: string) => setCart(cart.filter(i => getCartItemKey(i) !== key));
  const updateQuantity = (key: string, quantity: number) => {
    if (quantity <= 0) { removeFromCart(key); return; }
    setCart(cart.map(i => getCartItemKey(i) === key ? { ...i, quantity: Math.min(quantity, i.product.stock) } : i));
  };
  const getTotalPrice = () => cart.reduce((t, i) => t + getEffectivePrice(i.product, i.selectedAttributes) * i.quantity, 0);
  const getShippingCost = () => {
    let total = cfg.globalShippingCost || 0;
    cart.forEach(i => { if (i.product.shippingCost) total += i.product.shippingCost * i.quantity; });
    return total;
  };
  const getEffectiveTaxRate = () => cart.reduce((total, item) => {
    const taxRate = item.product.taxRate ?? cfg.globalTaxRate ?? 0;
    return total + (item.product.price * item.quantity) * (taxRate / 100);
  }, 0);
  const getGrandTotal = () => {
    const sub = getTotalPrice();
    const shipping = deliveryMethod === 'click_and_collect' ? 0 : getShippingCost();
    return sub + shipping + getEffectiveTaxRate();
  };
  const getTotalItems = () => cart.reduce((t, i) => t + i.quantity, 0);

  const toggleWishlist = useCallback((id: string) => {
    setWishlist(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  }, []);

  // ── Attribute selector UI ──
  const renderAttributeSelector = (product: Product, productId: string) => {
    if (!product.attributes?.length) return null;
    const selected = productAttrs[productId] || {};
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {(product.attributes as ProductAttribute[]).map(attr => {
          const selectedOptId = selected[attr.id];
          return (
            <div key={attr.id}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 6 }}>
                {attr.name}{attr.required && <span style={{ color: '#ef4444', marginLeft: 2 }}>*</span>}
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {attr.options.map(opt => {
                  const isSelected = selectedOptId === opt.id;
                  if (attr.type === 'color') {
                    return (
                      <button key={opt.id} title={opt.label + (opt.priceAdjustment ? ` (${opt.priceAdjustment > 0 ? '+' : ''}${opt.priceAdjustment}€)` : '')}
                        onClick={() => setProductAttrs(prev => ({ ...prev, [productId]: { ...(prev[productId] || {}), [attr.id]: isSelected ? '' : opt.id } }))}
                        style={{ width: 28, height: 28, borderRadius: '50%', border: isSelected ? '3px solid #1d4ed8' : '2px solid #e5e7eb', background: opt.color || '#000', cursor: 'pointer', outline: isSelected ? '2px solid #93c5fd' : 'none', outlineOffset: 2, position: 'relative' }}>
                        {isSelected && <span style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, color: '#fff', textShadow: '0 0 3px #000' }}>✓</span>}
                      </button>
                    );
                  }
                  return (
                    <button key={opt.id}
                      onClick={() => setProductAttrs(prev => ({ ...prev, [productId]: { ...(prev[productId] || {}), [attr.id]: isSelected ? '' : opt.id } }))}
                      style={{ padding: '5px 12px', borderRadius: 8, border: isSelected ? `2px solid ${accentColor}` : '1.5px solid #e5e7eb', background: isSelected ? accentColor : '#fff', color: isSelected ? accentText : '#374151', fontSize: 12, fontWeight: isSelected ? 700 : 500, cursor: 'pointer', transition: 'all 0.15s' }}>
                      {opt.label}{opt.priceAdjustment ? <span style={{ fontSize: 10, opacity: 0.75 }}> {opt.priceAdjustment > 0 ? '+' : ''}{opt.priceAdjustment}€</span> : null}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // ── Order logic ──
  const buildOrderAndHtml = (pm: string) => {
    if (cart.length === 0) return;
    if (!customerInfo.email || !customerInfo.name) {
      setPaymentError('Veuillez remplir votre nom complet et votre email avant de continuer.');
      return;
    }
    const order: Order = {
      id: `order_${Date.now()}`,
      items: cart,
      total: getGrandTotal(),
      taxTotal: getEffectiveTaxRate(),
      customerInfo,
      billingInfo: useDifferentBilling ? billingInfo : customerInfo,
      status: pm === 'cash' ? 'pending' : 'paid',
      paymentMethod: pm as any,
      createdAt: new Date().toISOString(),
      deliveryMethod,
    };
    const productListHTML = `<table width="100%" cellpadding="5" cellspacing="0" style="border-collapse:collapse;margin-top:10px;"><thead><tr><th style="text-align:left;border-bottom:2px solid #ddd;">Produit</th><th style="text-align:center;border-bottom:2px solid #ddd;">Qté</th><th style="text-align:right;border-bottom:2px solid #ddd;">Prix</th></tr></thead><tbody>${order.items.map(item => `<tr><td style="border-bottom:1px solid #eee;">${item.product.name}</td><td style="text-align:center;border-bottom:1px solid #eee;">${item.quantity}</td><td style="text-align:right;border-bottom:1px solid #eee;">${(item.product.price * item.quantity).toFixed(2)} ${cfg.currency}</td></tr>`).join('')}</tbody></table>`;
    return { order, productListHTML };
  };

  const finalizeOrder = async (pm: string) => {
    const data = buildOrderAndHtml(pm);
    if (!data) return;
    setIsProcessing(true);
    try {
      setPaymentError(null);
      await api.post('/ecommerce/send-confirmation', { ...data, config, ownerId: resolvedOwnerId });
      alert('Commande réussie ! Un email de confirmation vous a été envoyé.');
      setCart([]);
      try { if (cartStorageKey && cartStorage) cartStorage.removeItem(cartStorageKey); } catch { /* ignore */ }
      setShowCart(false);
      setShowCheckout(false);
      setCustomerInfo({ name: '', email: '', address: '', city: '', postalCode: '', country: defaultCountryLabel });
    } catch (error) {
      console.error('Erreur lors de la finalisation de la commande:', error);
      setPaymentError("Une erreur est survenue lors de l'envoi de l'email de confirmation.");
    } finally { setIsProcessing(false); }
  };

  const handlePaymentSuccess = (details: any) => finalizeOrder(details.method || paymentMethod);
  const handlePaymentError = (error: any) => setPaymentError(error.message || 'Une erreur inattendue est survenue.');

  const createStripeIntent = async () => {
    if (!stableStripeKey) { setPaymentError("Aucune clé Stripe n'est disponible."); return; }
    if (!resolvedOwnerId) { setPaymentError("Impossible d'identifier le vendeur."); return; }
    const data = buildOrderAndHtml('stripe');
    if (!data) return;
    setStripeBusy(true);
    try {
      const resp: any = await api.post('/ecommerce/stripe-intent', { ownerId: resolvedOwnerId, order: data.order, config });
      if (!resp?.clientSecret) throw new Error(resp?.message || 'Client secret manquant.');
      setStripeClientSecret(resp.clientSecret);
      setPendingOrder(data);
      if (pendingOrderKey && cartStorage) cartStorage.setItem(pendingOrderKey, JSON.stringify(data));
    } catch (err: any) {
      setPaymentError(err?.message || 'Erreur lors de la préparation Stripe.');
    } finally { setStripeBusy(false); }
  };

  const handleStripePaymentSuccess = async (paymentIntentId: string) => {
    if (!resolvedOwnerId) { setPaymentError("Impossible d'identifier le vendeur."); return; }
    const pending = pendingOrder || (() => {
      if (pendingOrderKey && cartStorage) { const raw = cartStorage.getItem(pendingOrderKey); if (raw) return JSON.parse(raw) as { order: Order; productListHTML: string }; }
      return null;
    })();
    if (!pending) { setPaymentError('Commande introuvable après paiement.'); return; }
    setIsProcessing(true);
    try {
      const confirmResp: any = await api.post('/ecommerce/stripe-confirm-intent', { ownerId: resolvedOwnerId, paymentIntentId });
      if (!confirmResp?.success) { setPaymentError(confirmResp?.message || 'Paiement non confirmé.'); return; }
      await api.post('/ecommerce/send-confirmation', { order: pending.order, config, productListHTML: pending.productListHTML, ownerId: resolvedOwnerId });
      alert('✅ Paiement confirmé. Un email a été envoyé.');
      setCart([]);
      try {
        if (cartStorageKey && cartStorage) cartStorage.removeItem(cartStorageKey);
        if (pendingOrderKey && cartStorage) cartStorage.removeItem(pendingOrderKey);
      } catch { /* ignore */ }
      setShowCart(false); setShowCheckout(false);
      setCustomerInfo({ name: '', email: '', address: '', city: '', postalCode: '', country: defaultCountryLabel });
      setStripeClientSecret(null); setPendingOrder(null);
    } catch (err: any) {
      setPaymentError(err?.message || 'Erreur de confirmation Stripe.');
    } finally { setIsProcessing(false); }
  };

  // ── Filtered & sorted products ──
  const normalizedCats: Array<{ id: string; name: string; color?: string; active?: boolean; badge?: string }> = (cfg.categories || []).map((c: any) =>
    typeof c === 'string' ? { id: c, name: c } : c
  ).filter((c: any) => c.active !== false);

  const filteredProducts = (Array.isArray(cfg.products) ? cfg.products : []).filter((p: Product) => {
    const matchesSearch = p.name.toLowerCase().includes(searchTerm.toLowerCase()) || p.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || p.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const sortedProducts = useMemo(() => {
    const sorted = [...filteredProducts];
    switch (sortBy) {
      case 'price_asc': return sorted.sort((a, b) => (a.salePrice || a.price) - (b.salePrice || b.price));
      case 'price_desc': return sorted.sort((a, b) => (b.salePrice || b.price) - (a.salePrice || a.price));
      case 'rating': return sorted.sort((a, b) => b.rating - a.rating);
      case 'name': return sorted.sort((a, b) => a.name.localeCompare(b.name));
      default: return sorted;
    }
  }, [filteredProducts, sortBy]);

  const availableCountries = Array.isArray(cfg.allowedCountries) && cfg.allowedCountries.length
    ? ALL_COUNTRIES.filter(c => cfg.allowedCountries!.includes(c.code))
    : ALL_COUNTRIES;

  // ── Style vars ──
  const bgValue = (cfg.backgroundColor ?? (cfg as any).background) || '#ffffff';
  const resolvedBg = bgValue === 'transparent' ? 'transparent' : bgValue;
  const textColor = cfg.textColor || '#212529';
  const accentColor = cfg.buttonColor || '#0d6efd';
  const accentText = cfg.buttonTextColor || '#ffffff';
  const cardBg = cfg.productBackgroundColor === 'transparent' ? 'transparent' : (cfg.productBackgroundColor || '#ffffff');

  const rootStyle: React.CSSProperties = {
    background: resolvedBg,
    backgroundColor: resolvedBg,
    color: textColor,
    fontFamily: cfg.fontFamily || undefined,
  };

  const btnStyle = (extra?: React.CSSProperties): React.CSSProperties => ({
    backgroundColor: accentColor,
    borderColor: accentColor,
    color: accentText,
    border: 'none',
    cursor: 'pointer',
    ...extra,
  });

  // Card image height
  type CardSize = 'lg' | 'md' | 'sm';
  const size: CardSize = (['lg', 'md', 'sm'].includes(cfg.productCardSize as any) ? cfg.productCardSize : 'lg') as CardSize;
  const imgH = size === 'sm' ? 160 : size === 'md' ? 200 : 240;

  // Grid columns — use narrower minimums on mobile so ≥2 products fit per row
  const colCount = cfg.columnsCount || 3;
  const minColWidth = isMobile
    ? (colCount === 2 ? 150 : colCount === 4 ? 130 : 155)
    : (colCount === 2 ? 280 : colCount === 4 ? 190 : 230);

  // ── Render helpers ──

  const renderBadge = (product: Product) => {
    if (!product.badge) return null;
    const badgeMap: Record<string, { label: string; bg: string }> = {
      new: { label: 'NOUVEAU', bg: '#10b981' },
      sale: { label: 'PROMO', bg: '#ef4444' },
      hot: { label: 'POPULAIRE', bg: '#f59e0b' },
    };
    const b = badgeMap[product.badge] || { label: product.badge.toUpperCase(), bg: '#6366f1' };
    return (
      <span style={{ position: 'absolute', top: 12, left: 12, padding: '4px 10px', borderRadius: 20, fontSize: 10, fontWeight: 800, background: b.bg, color: '#fff', letterSpacing: '0.05em', zIndex: 2 }}>
        {b.label}
      </span>
    );
  };

  const renderStarsRow = (rating: number) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
      <div style={{ display: 'flex', gap: 1 }}>
        {[1, 2, 3, 4, 5].map(s => (
          <span key={s} style={{ fontSize: 12, color: s <= rating ? '#fbbf24' : '#d1d5db' }}>★</span>
        ))}
      </div>
      <span style={{ fontSize: 11, color: '#9ca3af' }}>({rating}/5)</span>
    </div>
  );

  const renderPrice = (product: Product, large = false) => {
    const effectivePrice = product.salePrice && product.salePrice < product.price ? product.salePrice : null;
    return (
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
        <span style={{
          fontSize: large ? '1.75rem' : (cfg.priceSize || '1.1rem'),
          fontWeight: cfg.priceBold ? 800 : 700,
          color: effectivePrice ? '#ef4444' : (cfg.priceColor || accentColor),
          fontFamily: cfg.priceFontFamily !== 'inherit' ? cfg.priceFontFamily : undefined,
          fontStyle: cfg.priceItalic ? 'italic' : 'normal',
        }}>
          {(effectivePrice || product.price).toFixed(2)} {cfg.currency}
        </span>
        {effectivePrice && (
          <span style={{ fontSize: large ? '1rem' : '0.85rem', color: '#9ca3af', textDecoration: 'line-through' }}>
            {product.price.toFixed(2)} {cfg.currency}
          </span>
        )}
      </div>
    );
  };

  const renderMediaThumb = (url: string, size: number) => {
    if (isVideoUrl(url)) return <video src={url} style={{ width: size, height: size, objectFit: 'cover' }} muted playsInline />;
    if (isGlbUrl(url)) return <model-viewer src={url} style={{ width: size, height: size, background: '#f5f5f5' }} camera-controls auto-rotate />;
    if (isFbxUrl(url)) return <div style={{ width: size, height: size, background: '#e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 700, color: '#6b7280' }}>FBX</div>;
    return <img src={url} style={{ width: size, height: size, objectFit: 'cover' }} onError={handleImageError} alt="" />;
  };

  // ─── Grid Card ───────────────────────────────────────────────────────────────
  const renderGridCard = (product: Product) => {
    const imgs = getProductImages(product);
    const currentIdx = activeImageIndex[product.id] || 0;
    const main = imgs[currentIdx] || '/produit.png';
    const embedUrl = getVideoEmbedUrl(main);
    const isVid = isVideoUrl(main);
    const isGlb = isGlbUrl(main);
    const isFbx = isFbxUrl(main);
    const isHovered = hoveredCard === product.id;
    const isInWishlist = wishlist.includes(product.id);
    const inCartItem = cart.find(i => i.product.id === product.id);

    return (
      <div
        key={product.id}
        style={{
          borderRadius: RADIUS.lg,
          overflow: 'hidden',
          background: cardBg,
          boxShadow: isHovered ? '0 16px 48px rgba(0,0,0,0.14)' : '0 2px 12px rgba(0,0,0,0.07)',
          transition: 'all 0.3s cubic-bezier(0.4,0,0.2,1)',
          transform: isHovered ? 'translateY(-6px)' : 'translateY(0)',
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
        }}
        onMouseEnter={() => setHoveredCard(product.id)}
        onMouseLeave={() => setHoveredCard(null)}
      >
        {/* Image zone */}
        <div style={{ position: 'relative', height: imgH, overflow: 'hidden', background: cardBg || '#f9fafb', flexShrink: 0 }}>
          {embedUrl ? (
            <iframe src={embedUrl} title={product.name} style={{ width: '100%', height: '100%', border: 0 }} allowFullScreen />
          ) : isVid ? (
            <video src={main} style={{ width: '100%', height: '100%', objectFit: 'cover' }} muted playsInline />
          ) : isGlb ? (
            <model-viewer src={main} style={{ width: '100%', height: '100%' }} camera-controls auto-rotate exposure="1" shadow-intensity="0.6" />
          ) : isFbx ? (
            <FbxViewer url={main} height={imgH} />
          ) : (
            <img
              src={main}
              alt={product.name}
              style={{
                width: '100%', height: '100%', objectFit: 'cover', cursor: 'zoom-in',
                transform: isHovered ? 'scale(1.07)' : 'scale(1)',
                transition: 'transform 0.4s cubic-bezier(0.4,0,0.2,1)',
              }}
              onClick={() => openImageModal(product, currentIdx, imgs)}
              onError={handleImageError}
            />
          )}

          {/* Badge */}
          {renderBadge(product)}

          {/* Out of stock overlay */}
          {product.stock <= 0 && (
            <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 3 }}>
              <span style={{ color: '#fff', fontWeight: 700, fontSize: 13, background: 'rgba(239,68,68,0.9)', padding: '8px 18px', borderRadius: 30 }}>Rupture de stock</span>
            </div>
          )}

          {/* Wishlist button */}
          {cfg.showWishlist !== false && (
            <button
              onClick={(e) => { e.stopPropagation(); toggleWishlist(product.id); }}
              style={{
                position: 'absolute', top: 10, right: 10, width: 34, height: 34, borderRadius: '50%',
                border: 'none', background: 'rgba(255,255,255,0.92)', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16,
                boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
                transition: 'transform 0.2s',
                transform: isInWishlist ? 'scale(1.15)' : 'scale(1)',
                zIndex: 4,
              }}
            >
              {isInWishlist ? '❤️' : '🤍'}
            </button>
          )}

          {/* Hover overlay: quick actions */}
          {product.stock > 0 && (
            <div style={{
              position: 'absolute', bottom: 0, left: 0, right: 0, padding: '12px',
              background: 'linear-gradient(to top, rgba(0,0,0,0.65), transparent)',
              transform: isHovered ? 'translateY(0)' : 'translateY(100%)',
              transition: 'transform 0.3s ease',
              display: 'flex', gap: 8, zIndex: 2,
            }}>
              <button
                onClick={(e) => { e.stopPropagation(); if (product.attributes?.length) { setQuickViewProduct(product); } else { addToCart(product); } }}
                style={{ flex: 1, padding: '9px 12px', borderRadius: 10, border: 'none', cursor: 'pointer', background: accentColor, color: accentText, fontWeight: 700, fontSize: 13 }}
              >
                🛒 Ajouter
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); setQuickViewProduct(product); }}
                style={{ padding: '9px 13px', borderRadius: 10, border: 'none', cursor: 'pointer', background: 'rgba(255,255,255,0.2)', backdropFilter: 'blur(4px)', color: '#fff', fontSize: 17 }}
                title="Aperçu rapide"
              >
                👁️
              </button>
            </div>
          )}

          {/* Dot indicators */}
          {imgs.length > 1 && (
            <div style={{ position: 'absolute', bottom: product.stock > 0 ? 54 : 8, left: 0, right: 0, display: 'flex', gap: 5, justifyContent: 'center', zIndex: 2 }}>
              {imgs.map((_, idx) => (
                <div
                  key={idx}
                  onClick={(e) => { e.stopPropagation(); setActiveImageIndex(prev => ({ ...prev, [product.id]: idx })); }}
                  style={{ width: 6, height: 6, borderRadius: '50%', cursor: 'pointer', background: idx === currentIdx ? '#fff' : 'rgba(255,255,255,0.45)', transition: 'all 0.2s' }}
                />
              ))}
            </div>
          )}
        </div>

        {/* Card body */}
        <div style={{ padding: '14px 16px', flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
          {product.category && (
            <span style={{ fontSize: 10, fontWeight: 700, color: accentColor, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{product.category}</span>
          )}
          <h6 style={{ margin: 0, fontWeight: 700, fontSize: 14, color: textColor, lineHeight: 1.35, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' as any }}>
            {product.name}
          </h6>
          {cfg.showRatings !== false && product.rating > 0 && renderStarsRow(product.rating)}

          {/* Low stock warning */}
          {product.stock > 0 && product.stock <= 5 && (
            <span style={{ fontSize: 11, color: '#d97706', fontWeight: 600 }}>⚠️ Plus que {product.stock} en stock</span>
          )}

          {/* Filmstrip — n'apparaît que si le produit a plusieurs médias */}
          {imgs.length > 1 && (
            <div style={{ display: 'flex', gap: 4, overflowX: 'auto', paddingBottom: 2, scrollbarWidth: 'none', msOverflowStyle: 'none' as any }}>
              {imgs.map((media, idx) => (
                <div
                  key={idx}
                  onClick={(e) => { e.stopPropagation(); setActiveImageIndex(prev => ({ ...prev, [product.id]: idx })); }}
                  style={{
                    width: 34, height: 34, borderRadius: 5, overflow: 'hidden', flexShrink: 0, cursor: 'pointer',
                    border: `2px solid ${idx === currentIdx ? accentColor : '#e5e7eb'}`,
                    opacity: idx === currentIdx ? 1 : 0.7,
                    transition: 'border-color 0.15s, opacity 0.15s',
                  }}
                  title={`Média ${idx + 1}`}
                >
                  {renderMediaThumb(media, 34)}
                </div>
              ))}
            </div>
          )}

          <div style={{ marginTop: 'auto', paddingTop: 8, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
            {renderPrice(product)}
            {inCartItem ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, background: '#f3f4f6', borderRadius: 10, padding: '4px 8px' }}>
                <button onClick={(e) => { e.stopPropagation(); updateQuantity(product.id, inCartItem.quantity - 1); }} style={{ background: 'none', border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 15, color: '#374151', width: 22, lineHeight: 1 }}>−</button>
                <span style={{ fontWeight: 700, fontSize: 13, minWidth: 18, textAlign: 'center' }}>{inCartItem.quantity}</span>
                <button onClick={(e) => { e.stopPropagation(); addToCart(product); }} disabled={inCartItem.quantity >= product.stock} style={{ background: 'none', border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 15, color: '#374151', width: 22, lineHeight: 1, opacity: inCartItem.quantity >= product.stock ? 0.4 : 1 }}>+</button>
              </div>
            ) : (
              <button
                onClick={(e) => { e.stopPropagation(); if (product.attributes?.length) { setQuickViewProduct(product); } else { addToCart(product); } }}
                disabled={product.stock <= 0}
                style={btnStyle({ padding: '7px 14px', borderRadius: 10, fontSize: 12, fontWeight: 700, opacity: product.stock <= 0 ? 0.5 : 1 })}
              >
                {product.stock <= 0 ? '🚫' : (product.attributes?.length ? '⚙️ Choisir' : '+ Ajouter')}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  // ─── List Card ───────────────────────────────────────────────────────────────
  const renderListCard = (product: Product) => {
    const imgs = getProductImages(product);
    const main = imgs[0] || '/produit.png';
    const isInWishlist = wishlist.includes(product.id);
    const inCartItem = cart.find(i => i.product.id === product.id);

    return (
      <div
        key={product.id}
        style={{ display: 'flex', gap: 16, padding: '16px', borderRadius: RADIUS.lg, background: cardBg, boxShadow: '0 2px 12px rgba(0,0,0,0.06)', alignItems: 'center', transition: 'box-shadow 0.2s' }}
        onMouseEnter={(e) => (e.currentTarget.style.boxShadow = '0 6px 24px rgba(0,0,0,0.11)')}
        onMouseLeave={(e) => (e.currentTarget.style.boxShadow = '0 2px 12px rgba(0,0,0,0.06)')}
      >
        <div style={{ width: 88, height: 88, borderRadius: RADIUS.md, overflow: 'hidden', flexShrink: 0, cursor: 'zoom-in', background: '#f9fafb', position: 'relative' }} onClick={() => openImageModal(product, 0, imgs)}>
          {renderMediaThumb(main, 88)}
          {product.badge && (
            <div style={{ position: 'absolute', top: 4, left: 4, fontSize: 8, fontWeight: 800, padding: '2px 6px', borderRadius: 10, background: product.badge === 'sale' ? '#ef4444' : product.badge === 'new' ? '#10b981' : '#f59e0b', color: '#fff' }}>
              {product.badge.toUpperCase()}
            </div>
          )}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          {product.category && <span style={{ fontSize: 10, fontWeight: 700, color: accentColor, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{product.category}</span>}
          <h6 style={{ margin: '2px 0 4px', fontWeight: 700, color: textColor, fontSize: 15, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{product.name}</h6>
          {cfg.showRatings !== false && renderStarsRow(product.rating)}
          <p style={{ margin: '4px 0 0', fontSize: 12, color: '#6b7280', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' as any }}>
            {stripHtml(product.description || '')}
          </p>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 10, flexShrink: 0 }}>
          {renderPrice(product)}
          {product.stock <= 0 ? (
            <span style={{ fontSize: 12, color: '#ef4444', fontWeight: 600 }}>Indisponible</span>
          ) : inCartItem ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#f3f4f6', borderRadius: 10, padding: '4px 10px' }}>
              <button onClick={() => updateQuantity(product.id, inCartItem.quantity - 1)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 15, color: '#374151' }}>−</button>
              <span style={{ fontWeight: 700, minWidth: 20, textAlign: 'center' }}>{inCartItem.quantity}</span>
              <button onClick={() => addToCart(product)} disabled={inCartItem.quantity >= product.stock} style={{ background: 'none', border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 15, color: '#374151', opacity: inCartItem.quantity >= product.stock ? 0.4 : 1 }}>+</button>
            </div>
          ) : (
            <button onClick={() => { if (product.attributes?.length) { setQuickViewProduct(product); } else { addToCart(product); } }} style={btnStyle({ padding: '8px 18px', borderRadius: 10, fontSize: 13, fontWeight: 700 })}>{product.attributes?.length ? '⚙️ Choisir' : '+ Ajouter'}</button>
          )}
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {cfg.showWishlist !== false && (
              <button onClick={() => toggleWishlist(product.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, padding: 2 }}>{isInWishlist ? '❤️' : '🤍'}</button>
            )}
            <button onClick={() => setQuickViewProduct(product)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: '#6b7280', padding: 2 }} title="Voir détails">👁️</button>
          </div>
        </div>
      </div>
    );
  };

  // ─── Carousel Card (improved) ────────────────────────────────────────────────
  const renderCarousel = () => {
    if (sortedProducts.length === 0) return null;
    const product = sortedProducts[currentProductIndex];
    const imgs = getProductImages(product);
    const currentIdx = activeImageIndex[product.id] || 0;
    const main = imgs[currentIdx] || '/produit.png';
    const embedUrl = getVideoEmbedUrl(main);
    const isVid = isVideoUrl(main);
    const isGlb = isGlbUrl(main);
    const isFbx = isFbxUrl(main);
    const inCartItem = cart.find(i => i.product.id === product.id);
    const isInWishlist = wishlist.includes(product.id);

    return (
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <div style={{ maxWidth: 600, width: '100%' }}>
          <div style={{ borderRadius: RADIUS.xl, overflow: 'hidden', background: cardBg, boxShadow: '0 8px 40px rgba(0,0,0,0.1)' }}>
            {/* Image */}
            <div style={{ position: 'relative', height: imgH + 80 }}>
              {sortedProducts.length > 1 && (
                <>
                  <button onClick={() => setCurrentProductIndex(p => p > 0 ? p - 1 : sortedProducts.length - 1)} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', zIndex: 10, width: 40, height: 40, borderRadius: '50%', border: 'none', background: 'rgba(255,255,255,0.9)', cursor: 'pointer', fontSize: 18, boxShadow: '0 2px 12px rgba(0,0,0,0.15)' }}>❮</button>
                  <button onClick={() => setCurrentProductIndex(p => p < sortedProducts.length - 1 ? p + 1 : 0)} style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', zIndex: 10, width: 40, height: 40, borderRadius: '50%', border: 'none', background: 'rgba(255,255,255,0.9)', cursor: 'pointer', fontSize: 18, boxShadow: '0 2px 12px rgba(0,0,0,0.15)' }}>❯</button>
                </>
              )}
              {renderBadge(product)}
              {cfg.showWishlist !== false && (
                <button onClick={() => toggleWishlist(product.id)} style={{ position: 'absolute', top: 12, right: 12, zIndex: 10, width: 36, height: 36, borderRadius: '50%', border: 'none', background: 'rgba(255,255,255,0.9)', cursor: 'pointer', fontSize: 18, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>{isInWishlist ? '❤️' : '🤍'}</button>
              )}
              {embedUrl ? (
                <iframe src={embedUrl} title={product.name} style={{ width: '100%', height: '100%', border: 0 }} allowFullScreen />
              ) : isVid ? (
                <video src={main} style={{ width: '100%', height: '100%', objectFit: 'contain' }} controls muted playsInline />
              ) : isGlb ? (
                <model-viewer src={main} style={{ width: '100%', height: '100%' }} camera-controls auto-rotate exposure="1" shadow-intensity="0.6" />
              ) : isFbx ? (
                <FbxViewer url={main} height={imgH + 80} />
              ) : (
                <img src={main} alt={product.name} style={{ width: '100%', height: '100%', objectFit: 'contain', cursor: 'zoom-in' }} onClick={() => openImageModal(product, currentIdx, imgs)} onError={handleImageError} />
              )}
              {imgs.length > 1 && (
                <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, display: 'flex', gap: 4, padding: '6px', overflowX: 'auto', background: 'rgba(0,0,0,0.3)', justifyContent: 'center' }}>
                  {imgs.map((im, idx) => (
                    <div key={im + idx} onClick={(e) => { e.stopPropagation(); setActiveImageIndex(p => ({ ...p, [product.id]: idx })); }}
                      style={{ width: 32, height: 32, border: idx === currentIdx ? '2px solid #ffc107' : '1px solid #fff', borderRadius: 4, overflow: 'hidden', cursor: 'pointer', opacity: idx === currentIdx ? 1 : 0.7, background: '#000', flexShrink: 0 }}>
                      {renderMediaThumb(im, 32)}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Details */}
            <div style={{ padding: '20px 24px' }}>
              {/* Progress dots */}
              {sortedProducts.length > 1 && (
                <div style={{ display: 'flex', gap: 6, justifyContent: 'center', marginBottom: 16 }}>
                  {sortedProducts.map((_, idx) => (
                    <div key={idx} onClick={() => setCurrentProductIndex(idx)} style={{ width: idx === currentProductIndex ? 20 : 7, height: 7, borderRadius: 4, background: idx === currentProductIndex ? accentColor : '#d1d5db', cursor: 'pointer', transition: 'all 0.3s' }} />
                  ))}
                </div>
              )}
              {product.category && <span style={{ fontSize: 11, fontWeight: 700, color: accentColor, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{product.category}</span>}
              <h4 style={{ margin: '6px 0 8px', fontWeight: 700, color: textColor }}>{product.name}</h4>
              {cfg.showRatings !== false && <div style={{ marginBottom: 10 }}>{renderStarsRow(product.rating)}</div>}
              <div style={{ fontSize: 14, color: cfg.descriptionColor || '#6b7280', lineHeight: 1.6, marginBottom: 16 }} dangerouslySetInnerHTML={{ __html: decodeHtml(product.description || '') }} />
              {product.stock > 0 && product.stock <= 5 && <div style={{ marginBottom: 10, fontSize: 13, color: '#d97706', fontWeight: 600 }}>⚠️ Plus que {product.stock} en stock !</div>}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
                {renderPrice(product, true)}
                {product.stock <= 0 ? (
                  <span style={{ fontSize: 14, color: '#ef4444', fontWeight: 700 }}>🚫 Rupture de stock</span>
                ) : inCartItem ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#f3f4f6', borderRadius: 12, padding: '6px 14px' }}>
                    <button onClick={() => updateQuantity(product.id, inCartItem.quantity - 1)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 18, color: '#374151' }}>−</button>
                    <span style={{ fontWeight: 700, fontSize: 16, minWidth: 24, textAlign: 'center' }}>{inCartItem.quantity}</span>
                    <button onClick={() => addToCart(product)} disabled={inCartItem.quantity >= product.stock} style={{ background: 'none', border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 18, color: '#374151', opacity: inCartItem.quantity >= product.stock ? 0.4 : 1 }}>+</button>
                  </div>
                ) : (
                  <button onClick={() => addToCart(product)} style={btnStyle({ padding: '12px 28px', borderRadius: 14, fontWeight: 700, fontSize: 15 })}>🛒 Ajouter</button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ─── Checkout step validation ────────────────────────────────────────────────
  const canGoToStep2 = cart.length > 0;
  const canGoToStep3 = !!(customerInfo.name && customerInfo.email) && (!cfg.termsAndConditions || termsAccepted);

  // ─── RENDER ──────────────────────────────────────────────────────────────────
  return (
    <div className="ecommerce-shop-container p-3" style={rootStyle}>

      {/* ── HEADER ── */}
      <div style={{ textAlign: 'center', marginBottom: 28 }}>
        {cfg.shopName?.trim() && cfg.shopLogoUrl && (
          <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'center' }}>
            <img src={cfg.shopLogoUrl} alt={cfg.shopName} style={{ width: cfg.shopLogoWidth ? `${cfg.shopLogoWidth}px` : 'auto', height: cfg.shopLogoHeight ? `${cfg.shopLogoHeight}px` : 'auto', objectFit: 'contain', maxWidth: '100%' }} />
          </div>
        )}
        {cfg.shopName?.trim() && (
          <h2 style={{
            color: cfg.titleColor || textColor || accentColor,
            fontWeight: cfg.titleBold ? '700' : '400',
            fontStyle: cfg.titleItalic ? 'italic' : 'normal',
            fontSize: cfg.titleSize || '2rem',
            backgroundColor: cfg.titleBackgroundColor || 'transparent',
            padding: cfg.titleBackgroundColor && cfg.titleBackgroundColor !== 'transparent' ? '0.3rem 0.8rem' : undefined,
            borderRadius: cfg.titleBackgroundColor && cfg.titleBackgroundColor !== 'transparent' ? 12 : undefined,
            display: 'inline-block', maxWidth: '100%', margin: 0,
          }}>
            🛍️ {cfg.shopName}
          </h2>
        )}
        {cfg.shopDescription && (
          <div style={{
            color: (cfg as any).shopDescriptionColor || textColor || '#6c757d',
            fontSize: (cfg as any).shopDescriptionSize || '1rem',
            fontWeight: (cfg as any).shopDescriptionBold ? 'bold' : 'normal',
            fontStyle: (cfg as any).shopDescriptionItalic ? 'italic' : 'normal',
            backgroundColor: cfg.shopDescriptionBackgroundColor || 'transparent',
            padding: cfg.shopDescriptionBackgroundColor && cfg.shopDescriptionBackgroundColor !== 'transparent' ? '0.3rem 0.8rem' : undefined,
            borderRadius: cfg.shopDescriptionBackgroundColor && cfg.shopDescriptionBackgroundColor !== 'transparent' ? 10 : undefined,
            display: 'inline-block', maxWidth: '100%', marginTop: 8,
          }} dangerouslySetInnerHTML={{ __html: decodeHtml(cfg.shopDescription) }} />
        )}
      </div>

      {/* ── TOOLBAR ── */}
      <div style={{
        display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 20,
        backgroundColor: cfg.headerBackgroundColor === 'transparent' ? 'transparent' : (cfg.headerBackgroundColor || 'transparent'),
        padding: (cfg.headerBackgroundColor && cfg.headerBackgroundColor !== 'transparent') ? (isMobile ? '10px 12px' : '12px 16px') : 0,
        borderRadius: RADIUS.lg,
      }}>
        {/* Row 1 : Search + Cart */}
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          {cfg.showSearch !== false && (
            <div style={{ position: 'relative', flex: 1, minWidth: 0 }}>
              <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', fontSize: 15, pointerEvents: 'none' }}>🔍</span>
              <input
                type="text"
                placeholder="Rechercher..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{ ...inputStyle, paddingLeft: 34, paddingTop: 9, paddingBottom: 9 }}
              />
            </div>
          )}
          {/* Cart button — always visible */}
          <button
            onClick={() => setShowCart(true)}
            style={{
              ...btnStyle({ borderRadius: RADIUS.pill, padding: isMobile ? '9px 14px' : '8px 20px', fontWeight: 700, fontSize: isMobile ? 20 : 14, flexShrink: 0 }),
              position: 'relative', display: 'inline-flex', alignItems: 'center', gap: 6,
            }}
          >
            🛒
            {!isMobile && <AutoTranslateText sourceLang="fr">Panier</AutoTranslateText>}
            {getTotalItems() > 0 && (
              <span style={{
                background: '#ef4444', color: '#fff', borderRadius: '50%',
                width: 18, height: 18, display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 10, fontWeight: 800, position: 'absolute', top: -6, right: -6,
              }}>
                {getTotalItems()}
              </span>
            )}
          </button>
        </div>

        {/* Row 2 : Categories + Sort + View toggle */}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', overflowX: isMobile ? 'auto' : 'visible' }}>
          {/* Category pills — scrollable on mobile */}
          {cfg.showFilters !== false && normalizedCats.length > 0 && (
            <div style={{ display: 'flex', gap: 6, overflowX: 'auto', flexShrink: 0, maxWidth: isMobile ? 'calc(100vw - 130px)' : 'none', paddingBottom: isMobile ? 2 : 0 }}>
              {[{ id: 'all', name: 'Tous', color: undefined as string | undefined, badge: undefined as string | undefined }, ...normalizedCats].map((cat) => {
                const isActive = selectedCategory === cat.id || selectedCategory === cat.name;
                const catColor = cat.color || accentColor;
                return (
                  <button
                    key={cat.id}
                    onClick={() => setSelectedCategory(cat.name)}
                    style={{
                      padding: '6px 14px', borderRadius: RADIUS.pill, border: 'none', cursor: 'pointer',
                      fontSize: isMobile ? 11 : 12, fontWeight: 600, whiteSpace: 'nowrap', flexShrink: 0,
                      background: isActive ? catColor : '#f3f4f6',
                      color: isActive ? '#ffffff' : '#374151',
                      transition: 'all 0.2s',
                    }}
                  >
                    {cat.name}{cat.badge ? ` ${cat.badge}` : ''}
                  </button>
                );
              })}
            </div>
          )}

          {/* Right controls */}
          <div style={{ display: 'flex', gap: 6, marginLeft: 'auto', alignItems: 'center', flexShrink: 0 }}>
            {/* Sort */}
            {cfg.enableSort !== false && (
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as any)}
                style={{ padding: '7px 8px', borderRadius: RADIUS.md, border: '1.5px solid #e5e7eb', fontSize: 12, background: '#f9fafb', cursor: 'pointer', maxWidth: isMobile ? 90 : 'none' }}
              >
                <option value="default">Trier...</option>
                <option value="price_asc">Prix ↑</option>
                <option value="price_desc">Prix ↓</option>
                <option value="rating">Note ↓</option>
                <option value="name">A–Z</option>
              </select>
            )}
            {/* View mode toggle */}
            <div style={{ display: 'flex', background: '#f3f4f6', borderRadius: RADIUS.md, padding: 3, gap: 2 }}>
              {([
                { mode: 'grid', icon: '⊞', title: 'Grille' },
                { mode: 'list', icon: '≡', title: 'Liste' },
                { mode: 'carousel', icon: '▷', title: 'Carousel' },
              ] as const).map(({ mode, icon, title }) => (
                <button
                  key={mode}
                  onClick={() => setViewMode(mode)}
                  title={title}
                  style={{
                    width: 30, height: 30, borderRadius: 8, border: 'none', cursor: 'pointer',
                    background: viewMode === mode ? '#fff' : 'transparent',
                    boxShadow: viewMode === mode ? '0 1px 4px rgba(0,0,0,0.12)' : 'none',
                    fontSize: 14, transition: 'all 0.2s',
                    color: viewMode === mode ? '#374151' : '#9ca3af',
                  }}
                >
                  {icon}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ── PRODUCTS ── */}
      <div style={{ minHeight: 200 }}>
        {sortedProducts.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 0', color: '#9ca3af' }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>🔍</div>
            <h4 style={{ margin: 0, fontWeight: 700, color: '#6b7280' }}>
              <AutoTranslateText sourceLang="fr">Aucun produit trouvé</AutoTranslateText>
            </h4>
            <p style={{ margin: '8px 0 0', fontSize: 14 }}>
              {(Array.isArray(cfg.products) ? cfg.products.length : 0) === 0
                ? <AutoTranslateText sourceLang="fr">Ajoutez des produits dans la configuration</AutoTranslateText>
                : <AutoTranslateText sourceLang="fr">Essayez de modifier vos filtres</AutoTranslateText>
              }
            </p>
          </div>
        ) : viewMode === 'grid' ? (
          <div style={{ display: 'grid', gridTemplateColumns: `repeat(auto-fill, minmax(${minColWidth}px, 1fr))`, gap: 20 }}>
            {sortedProducts.map(renderGridCard)}
          </div>
        ) : viewMode === 'list' ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {sortedProducts.map(renderListCard)}
          </div>
        ) : (
          renderCarousel()
        )}
      </div>

      {/* ── FOOTER ── */}
      <div style={{
        marginTop: 40, paddingTop: 24, borderTop: '1px solid #e5e7eb', color: textColor,
        backgroundColor: cfg.footerBackgroundColor === 'transparent' ? 'transparent' : (cfg.footerBackgroundColor || 'transparent'),
        padding: (cfg.footerBackgroundColor && cfg.footerBackgroundColor !== 'transparent') ? '16px' : '24px 0 0',
        borderRadius: (cfg.footerBackgroundColor && cfg.footerBackgroundColor !== 'transparent') ? 12 : 0,
      }}>
        <div style={{ display: 'flex', justifyContent: 'center', gap: 40, textAlign: 'center', flexWrap: 'wrap' }}>
          <div><div style={{ fontSize: 22, fontWeight: 700, color: textColor }}>📦 {Array.isArray(cfg.products) ? cfg.products.length : 0}</div><div style={{ fontSize: 12, color: textColor, opacity: 0.7 }}>Produits</div></div>
          <div><div style={{ fontSize: 22, fontWeight: 700, color: textColor }}>🏷️ {(cfg.categories || []).length}</div><div style={{ fontSize: 12, color: textColor, opacity: 0.7 }}>Catégories</div></div>
          <div><div style={{ fontSize: 22, fontWeight: 700, color: textColor }}>🛒 {getTotalItems()}</div><div style={{ fontSize: 12, color: textColor, opacity: 0.7 }}>Articles</div></div>
          {wishlist.length > 0 && <div><div style={{ fontSize: 22, fontWeight: 700, color: textColor }}>❤️ {wishlist.length}</div><div style={{ fontSize: 12, color: textColor, opacity: 0.7 }}>Favoris</div></div>}
        </div>
        {(cfg.showTermsLink && (cfg.termsAndConditions || cfg.termsRgpd)) && (
          <div style={{ textAlign: 'center', marginTop: 16, display: 'flex', justifyContent: 'center', gap: 24, flexWrap: 'wrap' }}>
            {cfg.termsAndConditions && <button type="button" onClick={() => setShowTermsModal(true)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: textColor, opacity: 0.8 }}>📄 CGU / CGV</button>}
            {cfg.termsRgpd && <button type="button" onClick={() => setShowRgpdModal(true)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: textColor, opacity: 0.8 }}>🔒 RGPD</button>}
          </div>
        )}
      </div>

      {/* ══════════════════════════════════════════════════════════════════════ */}
      {/* ── CART DRAWER (portal) ── */}
      {createPortal(
        <>
          {/* Backdrop */}
          <div
            style={{
              position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1040,
              backdropFilter: 'blur(2px)', opacity: showCart ? 1 : 0,
              pointerEvents: showCart ? 'auto' : 'none', transition: 'opacity 0.3s',
            }}
            onClick={() => setShowCart(false)}
          />
          {/* Drawer — side on desktop, bottom sheet on mobile */}
          <div style={isMobile ? {
            position: 'fixed', bottom: 0, left: 0, right: 0, height: '85vh',
            background: '#fff', zIndex: 1041, display: 'flex', flexDirection: 'column',
            boxShadow: '0 -8px 48px rgba(0,0,0,0.18)',
            borderRadius: '20px 20px 0 0',
            transform: showCart ? 'translateY(0)' : 'translateY(100%)',
            transition: 'transform 0.35s cubic-bezier(0.4,0,0.2,1)',
          } : {
            position: 'fixed', top: 0, right: 0, height: '100vh', width: '100%', maxWidth: 420,
            background: '#fff', zIndex: 1041, display: 'flex', flexDirection: 'column',
            boxShadow: '-8px 0 48px rgba(0,0,0,0.18)',
            transform: showCart ? 'translateX(0)' : 'translateX(100%)',
            transition: 'transform 0.35s cubic-bezier(0.4,0,0.2,1)',
          }}>
            {/* Header */}
            <div style={{ padding: isMobile ? '14px 18px' : '20px 24px', borderBottom: '1px solid #f3f4f6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              {isMobile && <div style={{ width: 36, height: 4, borderRadius: 2, background: '#d1d5db', margin: '0 auto 14px', position: 'absolute', top: 8, left: '50%', transform: 'translateX(-50%)' }} />}
              <div>
                <h5 style={{ margin: 0, fontWeight: 700, fontSize: isMobile ? 16 : 18 }}>🛒 Votre Panier</h5>
                <span style={{ fontSize: 12, color: '#6b7280' }}>{getTotalItems()} article{getTotalItems() > 1 ? 's' : ''}</span>
              </div>
              <button onClick={() => setShowCart(false)} style={{ background: '#f3f4f6', border: 'none', borderRadius: 10, width: 36, height: 36, cursor: 'pointer', fontSize: 20, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
            </div>

            {/* Items */}
            <div style={{ flex: 1, overflowY: 'auto', padding: isMobile ? '12px 16px' : '16px 24px' }}>
              {cart.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '48px 0', color: '#9ca3af' }}>
                  <div style={{ fontSize: 52, marginBottom: 16 }}>🛒</div>
                  <p style={{ fontWeight: 600 }}>Votre panier est vide</p>
                  <button onClick={() => setShowCart(false)} style={btnStyle({ padding: '10px 24px', borderRadius: 12, fontWeight: 600, marginTop: 8 })}>Continuer mes achats</button>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  {cart.map(item => {
                    const itemKey = getCartItemKey(item);
                    const itemPrice = getEffectivePrice(item.product, item.selectedAttributes);
                    const attrLabels = item.selectedAttributes && item.product.attributes
                      ? (item.product.attributes as ProductAttribute[]).flatMap(a => {
                          const optId = item.selectedAttributes![a.id];
                          const opt = a.options.find(o => o.id === optId);
                          return opt ? [`${a.name}: ${opt.label}`] : [];
                        })
                      : [];
                    return (
                    <div key={itemKey} style={{ display: 'flex', gap: 12, alignItems: 'center', padding: '12px', background: '#f9fafb', borderRadius: 14 }}>
                      <div style={{ width: 60, height: 60, borderRadius: 10, overflow: 'hidden', flexShrink: 0 }}>
                        {renderMediaThumb(item.product.image || item.product.images?.[0] || '', 60)}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 700, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.product.name}</div>
                        {attrLabels.length > 0 && (
                          <div style={{ fontSize: 11, color: '#6b7280', marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{attrLabels.join(' · ')}</div>
                        )}
                        <div style={{ fontSize: 12, color: accentColor, fontWeight: 700, marginTop: 2 }}>{itemPrice.toFixed(2)} {cfg.currency}</div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#fff', borderRadius: 10, padding: '4px 8px', border: '1.5px solid #e5e7eb' }}>
                        <button onClick={() => updateQuantity(itemKey, item.quantity - 1)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 16, color: '#374151', width: 22, display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}>−</button>
                        <span style={{ fontWeight: 700, fontSize: 13, minWidth: 18, textAlign: 'center' }}>{item.quantity}</span>
                        <button onClick={() => updateQuantity(itemKey, item.quantity + 1)} disabled={item.quantity >= item.product.stock} style={{ background: 'none', border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 16, color: '#374151', width: 22, display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1, opacity: item.quantity >= item.product.stock ? 0.4 : 1 }}>+</button>
                      </div>
                      <button onClick={() => removeFromCart(itemKey)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', fontSize: 16, padding: 4, flexShrink: 0 }}>🗑️</button>
                    </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Footer */}
            {cart.length > 0 && (
              <div style={{ padding: isMobile ? '14px 16px' : '20px 24px', borderTop: '1px solid #f3f4f6', background: '#fafafa' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: '#6b7280', marginBottom: 6 }}>
                  <span>Sous-total</span><span>{getTotalPrice().toFixed(2)} {cfg.currency}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: '#6b7280', marginBottom: 6 }}>
                  <span>Livraison</span><span>{getShippingCost().toFixed(2)} {cfg.currency}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 800, fontSize: 20, marginBottom: 16, paddingTop: 10, borderTop: '1px solid #e5e7eb' }}>
                  <span>Total</span>
                  <span style={{ color: accentColor }}>{getGrandTotal().toFixed(2)} {cfg.currency}</span>
                </div>
                <button
                  onClick={() => { setShowCart(false); setShowCheckout(true); setCheckoutStep(1); }}
                  style={btnStyle({ width: '100%', padding: '14px', borderRadius: 14, fontWeight: 700, fontSize: 15 })}
                >
                  Commander →
                </button>
                <button onClick={() => setShowCart(false)} style={{ width: '100%', padding: '10px', background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', fontSize: 13, marginTop: 8 }}>
                  ← Continuer mes achats
                </button>
              </div>
            )}
          </div>
        </>,
        document.body
      )}

      {/* ── CHECKOUT MODAL (portal) ── */}
      {showCheckout && createPortal(
        <div style={{ position: 'fixed', inset: 0, zIndex: 1060, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(6px)', display: 'flex', alignItems: isMobile ? 'flex-end' : 'center', justifyContent: 'center', padding: isMobile ? 0 : 16 }}>
          <div style={{ background: '#fff', borderRadius: isMobile ? '20px 20px 0 0' : 24, width: '100%', maxWidth: isMobile ? '100%' : 780, maxHeight: isMobile ? '96vh' : '92vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 24px 80px rgba(0,0,0,0.25)' }}>

            {/* Step header */}
            <div style={{ padding: isMobile ? '14px 18px' : '20px 28px', borderBottom: '1px solid #f3f4f6' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <h5 style={{ margin: 0, fontWeight: 800, fontSize: isMobile ? 16 : 18 }}>Finaliser ma commande</h5>
                <button onClick={() => setShowCheckout(false)} style={{ background: '#f3f4f6', border: 'none', borderRadius: 10, width: 34, height: 34, cursor: 'pointer', fontSize: 18, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
              </div>
              {/* Step indicator */}
              <div style={{ display: 'flex', alignItems: 'center' }}>
                {[{ step: 1 as const, label: 'Récapitulatif' }, { step: 2 as const, label: 'Livraison' }, { step: 3 as const, label: 'Paiement' }].map(({ step, label }, idx) => (
                  <React.Fragment key={step}>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5 }}>
                      <div style={{ width: 34, height: 34, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 14, transition: 'all 0.3s', background: checkoutStep >= step ? accentColor : '#f3f4f6', color: checkoutStep >= step ? accentText : '#9ca3af' }}>
                        {checkoutStep > step ? '✓' : step}
                      </div>
                      {!isMobile && <span style={{ fontSize: 11, fontWeight: checkoutStep === step ? 700 : 400, color: checkoutStep >= step ? accentColor : '#9ca3af', whiteSpace: 'nowrap' }}>{label}</span>}
                    </div>
                    {idx < 2 && <div style={{ flex: 1, height: 2, margin: '0 8px', marginBottom: isMobile ? 0 : 20, background: checkoutStep > step ? accentColor : '#e5e7eb', transition: 'all 0.3s' }} />}
                  </React.Fragment>
                ))}
              </div>
            </div>

            {/* Step content */}
            <div style={{ flex: 1, overflowY: 'auto', padding: isMobile ? '16px 18px' : '20px 28px' }}>

              {/* STEP 1: Summary */}
              {checkoutStep === 1 && (
                <div>
                  {/* Delivery mode banner */}
                  {(() => {
                    const isCC = deliveryMethod === 'click_and_collect';
                    const collectAddr = cfg.clickAndCollect?.address;
                    const mapsUrl = collectAddr ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(collectAddr)}` : null;
                    return (
                      <div style={{
                        display: 'flex', alignItems: 'flex-start', gap: 10,
                        padding: '10px 14px', marginBottom: 16, borderRadius: 10,
                        background: isCC ? '#f0f9ff' : '#f0fdf4',
                        border: `1px solid ${isCC ? '#bae6fd' : '#bbf7d0'}`,
                      }}>
                        <span style={{ fontSize: 22, flexShrink: 0 }}>{isCC ? '🏪' : '🚚'}</span>
                        <div style={{ fontSize: 13, lineHeight: 1.5 }}>
                          <div style={{ fontWeight: 700, color: isCC ? '#0369a1' : '#15803d' }}>
                            {isCC ? 'Retrait en boutique (Click & Collect)' : 'Expédition à domicile'}
                          </div>
                          {isCC && collectAddr && (
                            <div style={{ color: '#374151', marginTop: 2 }}>
                              📍 {collectAddr}
                              {mapsUrl && (
                                <> · <a href={mapsUrl} target="_blank" rel="noreferrer" style={{ color: '#0369a1', fontWeight: 600 }}>Voir sur Maps</a></>
                              )}
                            </div>
                          )}
                          {isCC && cfg.clickAndCollect?.instructions && (
                            <div style={{ color: '#6b7280', marginTop: 2, fontSize: 12 }}>{cfg.clickAndCollect.instructions}</div>
                          )}
                          {!isCC && <div style={{ color: '#6b7280', marginTop: 2 }}>Frais de livraison : {getShippingCost().toFixed(2)} {cfg.currency}</div>}
                        </div>
                      </div>
                    );
                  })()}
                  <h6 style={{ fontWeight: 700, marginBottom: 16 }}>Votre commande ({getTotalItems()} article{getTotalItems() > 1 ? 's' : ''})</h6>
                  {cart.map(item => (
                    <div key={item.product.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid #f3f4f6' }}>
                      <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                        <div style={{ width: 48, height: 48, borderRadius: 8, overflow: 'hidden', flexShrink: 0 }}>{renderMediaThumb(item.product.image || '', 48)}</div>
                        <div>
                          <div style={{ fontWeight: 600, fontSize: 14 }}>{item.product.name}</div>
                          <div style={{ fontSize: 12, color: '#6b7280' }}>Qté : {item.quantity}</div>
                        </div>
                      </div>
                      <span style={{ fontWeight: 700, color: accentColor }}>{(item.product.price * item.quantity).toFixed(2)} {cfg.currency}</span>
                    </div>
                  ))}
                  <div style={{ marginTop: 16, padding: 16, background: '#f9fafb', borderRadius: 14 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: '#6b7280', marginBottom: 6 }}><span>Sous-total</span><span>{getTotalPrice().toFixed(2)} {cfg.currency}</span></div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: '#6b7280', marginBottom: 6 }}><span>Livraison</span><span>{getShippingCost().toFixed(2)} {cfg.currency}</span></div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: '#6b7280', marginBottom: 10 }}><span>TVA</span><span>{getEffectiveTaxRate().toFixed(2)} {cfg.currency}</span></div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 800, fontSize: 20, paddingTop: 10, borderTop: '1px solid #e5e7eb' }}>
                      <span>Total</span><span style={{ color: accentColor }}>{getGrandTotal().toFixed(2)} {cfg.currency}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* STEP 2: Delivery info */}
              {checkoutStep === 2 && (() => {
                // Computed flags derived from config + state
                const showDeliveryChoice = cfg.deliveryType === 'both';
                const showShippingFields = cfg.deliveryType !== 'click_and_collect' && deliveryMethod === 'shipping';
                const showCollectInfo = deliveryMethod === 'click_and_collect' || cfg.deliveryType === 'click_and_collect';
                // Compact field style
                const f: React.CSSProperties = { ...inputStyle, padding: '5px 8px', fontSize: 12, borderRadius: RADIUS.sm };
                const lbl: React.CSSProperties = { display: 'block', fontSize: 11, fontWeight: 600, marginBottom: 2, color: '#374151' };
                return (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {/* Delivery choice — shown only when 'both' */}
                    {showDeliveryChoice && (
                      <div style={{ display: 'flex', gap: 8 }}>
                        {(['shipping', 'click_and_collect'] as const).map(method => (
                          <div key={method} onClick={() => setDeliveryMethod(method)} style={{ flex: 1, padding: '8px 10px', borderRadius: 10, cursor: 'pointer', textAlign: 'center', border: `2px solid ${deliveryMethod === method ? accentColor : '#e5e7eb'}`, background: deliveryMethod === method ? `${accentColor}12` : '#fff', transition: 'all 0.2s' }}>
                            <div style={{ fontSize: 16, marginBottom: 2 }}>{method === 'shipping' ? '🚚' : '🏪'}</div>
                            <div style={{ fontWeight: 700, fontSize: 11 }}>{method === 'shipping' ? 'Expédition' : 'Click & Collect'}</div>
                            <div style={{ fontSize: 10, color: '#6b7280', marginTop: 1 }}>{method === 'shipping' ? `${getShippingCost().toFixed(2)} ${cfg.currency}` : 'Gratuit'}</div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Compact identity fields — always visible */}
                    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 8 }}>
                      <div>
                        <label style={lbl}>Nom complet *</label>
                        <input type="text" value={customerInfo.name} onChange={(e) => setCustomerInfo({ ...customerInfo, name: e.target.value })} style={f} />
                      </div>
                      <div>
                        <label style={lbl}>Email *</label>
                        <input type="email" value={customerInfo.email} onChange={(e) => setCustomerInfo({ ...customerInfo, email: e.target.value })} style={f} />
                      </div>
                    </div>

                    {/* Shipping address — only when mode includes shipping and user chose shipping */}
                    {showShippingFields && (
                      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 8 }}>
                        <div style={{ gridColumn: '1 / -1' }}>
                          <label style={lbl}>Adresse</label>
                          <input type="text" value={customerInfo.address} onChange={(e) => setCustomerInfo({ ...customerInfo, address: e.target.value })} style={f} />
                        </div>
                        <div>
                          <label style={lbl}>Ville</label>
                          <input type="text" value={customerInfo.city} onChange={(e) => setCustomerInfo({ ...customerInfo, city: e.target.value })} style={f} />
                        </div>
                        <div>
                          <label style={lbl}>Code Postal</label>
                          <input type="text" value={customerInfo.postalCode} onChange={(e) => setCustomerInfo({ ...customerInfo, postalCode: e.target.value })} style={f} />
                        </div>
                        <div style={{ gridColumn: '1 / -1' }}>
                          <label style={lbl}>Pays</label>
                          <select value={customerInfo.country} onChange={(e) => setCustomerInfo({ ...customerInfo, country: e.target.value })} style={f}>
                            {availableCountries.map(c => <option key={c.code} value={c.label}>{c.flag} {c.label}</option>)}
                          </select>
                        </div>

                        {/* Different billing address */}
                        <div style={{ gridColumn: '1 / -1' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }} onClick={() => setUseDifferentBilling(!useDifferentBilling)}>
                            <div style={{ width: 16, height: 16, borderRadius: 4, border: `2px solid ${useDifferentBilling ? accentColor : '#d1d5db'}`, background: useDifferentBilling ? accentColor : '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                              {useDifferentBilling && <span style={{ color: '#fff', fontSize: 10, lineHeight: 1 }}>✓</span>}
                            </div>
                            <span style={{ fontSize: 12, color: '#374151' }}>Adresse de facturation différente</span>
                          </div>
                          {useDifferentBilling && (
                            <div style={{ marginTop: 8, padding: 10, background: '#f9fafb', borderRadius: 10, display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 8 }}>
                              <div style={{ gridColumn: '1 / -1' }}>
                                <label style={lbl}>Adresse de facturation</label>
                                <input type="text" value={billingInfo.address} onChange={(e) => setBillingInfo({ ...billingInfo, address: e.target.value })} style={f} />
                              </div>
                              <div>
                                <label style={lbl}>Ville</label>
                                <input type="text" value={billingInfo.city} onChange={(e) => setBillingInfo({ ...billingInfo, city: e.target.value })} style={f} />
                              </div>
                              <div>
                                <label style={lbl}>Code Postal</label>
                                <input type="text" value={billingInfo.postalCode} onChange={(e) => setBillingInfo({ ...billingInfo, postalCode: e.target.value })} style={f} />
                              </div>
                              <div style={{ gridColumn: '1 / -1' }}>
                                <label style={lbl}>Pays</label>
                                <select value={billingInfo.country} onChange={(e) => setBillingInfo({ ...billingInfo, country: e.target.value })} style={f}>
                                  {availableCountries.map(c => <option key={c.code} value={c.label}>{c.flag} {c.label}</option>)}
                                </select>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* CGV */}
                    {cfg.termsAndConditions && (
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                        <div onClick={() => setTermsAccepted(!termsAccepted)} style={{ width: 16, height: 16, borderRadius: 4, border: `2px solid ${termsAccepted ? accentColor : '#d1d5db'}`, background: termsAccepted ? accentColor : '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, cursor: 'pointer', marginTop: 2 }}>
                          {termsAccepted && <span style={{ color: '#fff', fontSize: 10 }}>✓</span>}
                        </div>
                        <span style={{ fontSize: 12, color: '#374151', lineHeight: 1.5 }}>
                          J'accepte les <a href="#" onClick={(e) => { e.preventDefault(); setShowTermsModal(true); }} style={{ color: accentColor }}>Conditions Générales de Vente</a>
                        </span>
                      </div>
                    )}

                    {/* Click & Collect address + map */}
                    {showCollectInfo && cfg.clickAndCollect?.address && (
                      <div>
                        <div style={{ padding: '10px 12px', background: '#f0f9ff', borderRadius: 10, marginBottom: 8 }}>
                          <div style={{ fontWeight: 700, fontSize: 12, marginBottom: 3 }}>📍 Adresse de retrait</div>
                          <div style={{ fontSize: 12, color: '#374151' }}>{cfg.clickAndCollect.address}</div>
                          {cfg.clickAndCollect.instructions && <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>{cfg.clickAndCollect.instructions}</div>}
                        </div>
                        <div id="click-collect-map" style={{ height: 180, borderRadius: 10, background: '#e5e7eb' }} />
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* STEP 3: Payment */}
              {checkoutStep === 3 && (
                <div>
                  <h6 style={{ fontWeight: 700, marginBottom: 16 }}>Mode de paiement</h6>
                  {availablePaymentMethods.length === 0 ? (
                    <div style={{ padding: 16, background: '#fef2f2', borderRadius: 12, color: '#dc2626', fontSize: 14 }}>Aucun moyen de paiement n'est configuré.</div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 20 }}>
                      {availablePaymentMethods.map(method => (
                        <div key={method} onClick={() => { setPaymentMethod(method); setPaymentError(null); }} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 18px', borderRadius: 14, cursor: 'pointer', border: `2px solid ${paymentMethod === method ? accentColor : '#e5e7eb'}`, background: paymentMethod === method ? `${accentColor}10` : '#fff', transition: 'all 0.2s' }}>
                          <span style={{ fontSize: 22, flexShrink: 0 }}>{method === 'stripe' ? '💳' : method === 'paypal' ? '🅿️' : '💵'}</span>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: 700, fontSize: 14 }}>{method === 'stripe' ? 'Carte bancaire (Stripe)' : method === 'paypal' ? 'PayPal' : 'Paiement à la livraison'}</div>
                            <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>{method === 'stripe' ? 'Paiement sécurisé 3D-Secure' : method === 'paypal' ? 'Compte PayPal ou carte bancaire' : 'Réglez à la réception de votre commande'}</div>
                          </div>
                          <div style={{ width: 20, height: 20, borderRadius: '50%', border: `2px solid ${paymentMethod === method ? accentColor : '#d1d5db'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            {paymentMethod === method && <div style={{ width: 10, height: 10, borderRadius: '50%', background: accentColor }} />}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Mini recap */}
                  <div style={{ padding: '14px 18px', background: '#f9fafb', borderRadius: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                    <span style={{ fontWeight: 700 }}>Total à payer</span>
                    <span style={{ fontWeight: 800, fontSize: 20, color: accentColor }}>{getGrandTotal().toFixed(2)} {cfg.currency}</span>
                  </div>

                  {paymentError && <div style={{ padding: '12px 16px', borderRadius: 10, background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', fontSize: 14, marginBottom: 16 }}>{paymentError}</div>}

                  {isProcessing ? (
                    <div style={{ textAlign: 'center', padding: 24 }}>
                      <div className="spinner-border" role="status" />
                      <p style={{ marginTop: 12, color: '#6b7280' }}>Finalisation de la commande...</p>
                    </div>
                  ) : (
                    <div style={{ opacity: (cfg.termsAndConditions && !termsAccepted) ? 0.5 : 1, pointerEvents: (cfg.termsAndConditions && !termsAccepted) ? 'none' : 'auto' }}>
                      {paymentMethod === 'stripe' && (
                        <>
                          {!stripeClientSecret && (
                            <button onClick={createStripeIntent} disabled={stripeBusy || !stableStripeKey} style={btnStyle({ width: '100%', padding: '14px', borderRadius: 14, fontWeight: 700, fontSize: 15 })}>
                              {stripeBusy ? '⏳ Préparation...' : '💳 Continuer avec Stripe'}
                            </button>
                          )}
                          {stripeClientSecret && stripePromise && stripeElementsOptions && (
                            <Elements stripe={stripePromise} options={stripeElementsOptions}>
                              <StripePaymentElementForm onSuccess={handleStripePaymentSuccess} onError={handlePaymentError} submitLabel="Payer par carte" disabled={isProcessing} />
                            </Elements>
                          )}
                        </>
                      )}
                      {paymentMethod === 'paypal' && effectivePaypalKey && (
                        <PayPalScriptProvider options={{ clientId: effectivePaypalKey, currency: cfg.currency }}>
                          <PayPalCheckoutButtons amount={getGrandTotal().toFixed(2)} currency={cfg.currency} onSuccess={(details) => handlePaymentSuccess({ ...details, method: 'paypal' })} onError={handlePaymentError} />
                        </PayPalScriptProvider>
                      )}
                      {paymentMethod === 'cash' && (
                        <button onClick={() => finalizeOrder('cash')} style={btnStyle({ width: '100%', padding: '14px', borderRadius: 14, fontWeight: 700, fontSize: 15 })}>
                          💵 Confirmer la commande
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Navigation footer */}
            <div style={{ padding: isMobile ? '12px 18px' : '14px 28px', borderTop: '1px solid #f3f4f6', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#fafafa' }}>
              <button
                onClick={() => { if (checkoutStep > 1) setCheckoutStep(prev => (prev - 1) as 1 | 2 | 3); else { setShowCheckout(false); setShowCart(true); } }}
                style={{ padding: '9px 20px', borderRadius: 12, border: '1.5px solid #e5e7eb', background: '#fff', cursor: 'pointer', fontWeight: 600, fontSize: 14, color: '#374151' }}
              >
                ← {checkoutStep === 1 ? 'Retour au panier' : 'Retour'}
              </button>
              {checkoutStep < 3 && (
                <button
                  onClick={() => {
                    if (checkoutStep === 1 && canGoToStep2) setCheckoutStep(2);
                    else if (checkoutStep === 2 && canGoToStep3) setCheckoutStep(3);
                    else if (checkoutStep === 2 && !canGoToStep3) setPaymentError('Veuillez remplir votre nom, email et accepter les CGV.');
                  }}
                  disabled={checkoutStep === 1 && !canGoToStep2}
                  style={btnStyle({ padding: '9px 24px', borderRadius: 12, fontWeight: 700, fontSize: 14, opacity: (checkoutStep === 1 && !canGoToStep2) ? 0.6 : 1 })}
                >
                  Continuer →
                </button>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* ── QUICK VIEW MODAL (portal) ── */}
      {quickViewProduct && createPortal(
        <div style={{ position: 'fixed', inset: 0, zIndex: 1070, background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(6px)', display: 'flex', alignItems: isMobile ? 'flex-end' : 'center', justifyContent: 'center', padding: isMobile ? 0 : 16 }} onClick={() => setQuickViewProduct(null)}>
          <div style={{ background: '#fff', borderRadius: isMobile ? '20px 20px 0 0' : 24, width: '100%', maxWidth: isMobile ? '100%' : 840, maxHeight: isMobile ? '92vh' : '90vh', overflow: 'hidden', display: 'flex', flexDirection: isMobile ? 'column' : 'row' }} onClick={e => e.stopPropagation()}>
            {/* Images panel */}
            <div style={{ flex: isMobile ? 'none' : 1, background: '#f9fafb', position: 'relative', height: isMobile ? 220 : 'auto', minHeight: isMobile ? 'auto' : 300, display: 'flex', flexDirection: 'column' }}>
              {(() => {
                const imgs = getProductImages(quickViewProduct);
                const currentIdx = activeImageIndex[quickViewProduct.id] || 0;
                const main = imgs[currentIdx] || '/produit.png';
                const imgMaxH = isMobile ? 180 : 400;
                return (
                  <>
                    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: isMobile ? 12 : 20 }}>
                      {isVideoUrl(main) ? (
                        <video src={main} style={{ maxWidth: '100%', maxHeight: imgMaxH, objectFit: 'contain' }} controls muted playsInline />
                      ) : isGlbUrl(main) ? (
                        <model-viewer src={main} style={{ width: '100%', height: imgMaxH }} camera-controls auto-rotate />
                      ) : isFbxUrl(main) ? (
                        <FbxViewer url={main} height={imgMaxH} />
                      ) : (
                        <img src={main} alt={quickViewProduct.name} style={{ maxWidth: '100%', maxHeight: imgMaxH, objectFit: 'contain' }} onError={handleImageError} />
                      )}
                    </div>
                    {imgs.length > 1 && (
                      <div style={{ display: 'flex', gap: 8, padding: '12px', justifyContent: 'center', background: 'rgba(0,0,0,0.05)', flexWrap: 'wrap' }}>
                        {imgs.map((im, idx) => (
                          <div key={idx} onClick={() => setActiveImageIndex(p => ({ ...p, [quickViewProduct.id]: idx }))} style={{ width: 52, height: 52, borderRadius: 8, overflow: 'hidden', cursor: 'pointer', border: `2px solid ${idx === currentIdx ? accentColor : '#e5e7eb'}`, flexShrink: 0 }}>
                            {renderMediaThumb(im, 52)}
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                );
              })()}
            </div>

            {/* Details panel */}
            <div style={{ width: isMobile ? '100%' : 320, flex: isMobile ? 1 : 'none', padding: isMobile ? '16px 18px' : '24px', display: 'flex', flexDirection: 'column', gap: 12, overflowY: 'auto' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ flex: 1 }}>
                  {quickViewProduct.category && <span style={{ fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em', color: accentColor }}>{quickViewProduct.category}</span>}
                  {renderBadge(quickViewProduct)}
                </div>
                <button onClick={() => setQuickViewProduct(null)} style={{ background: '#f3f4f6', border: 'none', borderRadius: 10, width: 32, height: 32, cursor: 'pointer', fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>×</button>
              </div>
              <h4 style={{ margin: 0, fontWeight: 800, fontSize: 20, color: textColor, lineHeight: 1.3 }}>{quickViewProduct.name}</h4>
              {cfg.showRatings !== false && renderStarsRow(quickViewProduct.rating)}
              {renderPrice(quickViewProduct, true)}

              <div style={{ fontSize: 14, color: '#6b7280', lineHeight: 1.65, overflowY: 'auto', maxHeight: isMobile ? 80 : 160 }} dangerouslySetInnerHTML={{ __html: decodeHtml(quickViewProduct.description || '') }} />

              {quickViewProduct.stock > 0 && quickViewProduct.stock <= 5 && (
                <div style={{ padding: '8px 12px', background: '#fff7ed', borderRadius: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span>⚠️</span><span style={{ fontSize: 13, color: '#c2410c', fontWeight: 600 }}>Plus que {quickViewProduct.stock} en stock !</span>
                </div>
              )}
              {quickViewProduct.stock <= 0 && (
                <div style={{ padding: '8px 12px', background: '#fef2f2', borderRadius: 8 }}>
                  <span style={{ fontSize: 13, color: '#dc2626', fontWeight: 600 }}>🚫 Rupture de stock</span>
                </div>
              )}

              {renderAttributeSelector(quickViewProduct, quickViewProduct.id)}

              {(() => {
                const selAttrs = productAttrs[quickViewProduct.id] || {};
                const effectivePrice = getEffectivePrice(quickViewProduct, selAttrs);
                const canAdd = quickViewProduct.stock > 0 && hasRequiredAttrs(quickViewProduct, selAttrs);
                const missingRequired = quickViewProduct.stock > 0 && !hasRequiredAttrs(quickViewProduct, selAttrs);
                return (
                  <>
                    {missingRequired && (
                      <div style={{ fontSize: 12, color: '#ef4444', padding: '6px 10px', background: '#fef2f2', borderRadius: 8 }}>
                        ⚠️ Veuillez sélectionner toutes les options obligatoires
                      </div>
                    )}
                    {quickViewProduct.attributes?.length && effectivePrice !== (quickViewProduct.salePrice ?? quickViewProduct.price) && (
                      <div style={{ fontSize: 13, fontWeight: 700, color: accentColor }}>
                        Prix avec options : {effectivePrice.toFixed(2)} {cfg.currency}
                      </div>
                    )}
                    <div style={{ marginTop: 'auto', display: 'flex', gap: 10 }}>
                      <button
                        onClick={() => { if (canAdd) { addToCart(quickViewProduct, selAttrs); setQuickViewProduct(null); } }}
                        disabled={!canAdd}
                        style={btnStyle({ flex: 1, padding: '12px', borderRadius: 12, fontWeight: 700, fontSize: 14, opacity: !canAdd ? 0.5 : 1 })}
                      >
                        {quickViewProduct.stock <= 0 ? '🚫 Indisponible' : '🛒 Ajouter au panier'}
                      </button>
                      {cfg.showWishlist !== false && (
                        <button onClick={() => toggleWishlist(quickViewProduct.id)} style={{ width: 46, height: 46, borderRadius: 12, border: '1.5px solid #e5e7eb', background: '#fff', cursor: 'pointer', fontSize: 20, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          {wishlist.includes(quickViewProduct.id) ? '❤️' : '🤍'}
                        </button>
                      )}
                    </div>
                  </>
                );
              })()}
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* ── IMAGE MODAL (portal) ── */}
      {imageModal && createPortal(
        <div className="ecom-image-modal-backdrop" onClick={() => setImageModal(null)}>
          <div className="ecom-image-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="ecom-image-modal-toolbar">
              <button type="button" onClick={() => setImageModal(m => m ? { ...m, index: (m.index - 1 + m.images.length) % m.images.length } : m)} disabled={imageModal.images.length < 2}>◀</button>
              <span style={{ fontSize: 13 }}>{imageModal.index + 1} / {imageModal.images.length}</span>
              <button type="button" onClick={() => setImageModal(m => m ? { ...m, index: (m.index + 1) % m.images.length } : m)} disabled={imageModal.images.length < 2}>▶</button>
              <div className="flex-spacer" />
              <button type="button" onClick={() => setZoom(z => Math.max(1, +(z - 0.25).toFixed(2)))} disabled={!modalIsZoomable || zoom <= 1}>➖</button>
              <span style={{ minWidth: 50, textAlign: 'center' }}>{Math.round(zoom * 100)}%</span>
              <button type="button" onClick={() => setZoom(z => Math.min(3, +(z + 0.25).toFixed(2)))} disabled={!modalIsZoomable || zoom >= 3}>➕</button>
              <button type="button" onClick={() => setImageModal(null)}>✕</button>
            </div>
            <div className="ecom-image-modal-stage" onWheel={(e) => { if (e.ctrlKey || !modalIsZoomable) return; if (e.deltaY > 0) setZoom(z => Math.max(1, +(z - 0.15).toFixed(2))); else setZoom(z => Math.min(3, +(z + 0.15).toFixed(2))); }}>
              {modalEmbedUrl ? (
                <iframe src={modalEmbedUrl} title="media" className="w-100 h-100 border-0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
              ) : modalIsVideo ? (
                <video src={modalMedia || ''} controls autoPlay muted playsInline className="w-100 h-100" style={{ objectFit: 'contain' }} />
              ) : modalIsGlb ? (
                <model-viewer src={modalMedia || ''} className="w-100 h-100" style={{ background: '#f5f5f5' }} camera-controls auto-rotate exposure="1" shadow-intensity="0.6" />
              ) : modalIsFbx ? (
                <FbxViewer url={modalMedia || ''} height={520} />
              ) : (
                <img src={imageModal.images[imageModal.index]} alt="zoom" style={{ maxWidth: '100%', maxHeight: '100%', transform: `scale(${zoom})`, transformOrigin: 'center center', transition: 'transform .15s' }} onDragStart={(e) => e.preventDefault()} />
              )}
            </div>
            {imageModal.images.length > 1 && (
              <div className="ecom-image-modal-thumbs">
                {imageModal.images.map((im, idx) => (
                  <div key={im + idx} style={{ width: 56, height: 56, border: idx === imageModal.index ? '3px solid #ffc107' : '2px solid #fff', borderRadius: 6, cursor: 'pointer', opacity: idx === imageModal.index ? 1 : 0.75, overflow: 'hidden', background: '#000' }} onClick={() => setImageModal(m => m ? { ...m, index: idx } : m)}>
                    {renderMediaThumb(im, 56)}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>,
        document.body
      )}

      {/* ── TERMS MODAL ── */}
      {showTermsModal && createPortal(
        <div className="ecom-terms-modal-backdrop" onClick={() => setShowTermsModal(false)}>
          <div className="ecom-terms-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <div className="ecom-terms-modal-header">
              <h5 className="mb-0"><AutoTranslateText sourceLang="fr">Conditions Générales de Vente</AutoTranslateText></h5>
              <button type="button" className="btn-close btn-close-white" onClick={() => setShowTermsModal(false)} style={{ filter: 'invert(1)' }} />
            </div>
            <div className="ecom-terms-modal-body">
              <div style={{ whiteSpace: 'pre-wrap', fontSize: 14, lineHeight: 1.5 }} dangerouslySetInnerHTML={{ __html: (cfg.termsAndConditions || '').replace(/\n/g, '<br/>') }} />
            </div>
            <div className="ecom-terms-modal-footer">
              <button type="button" className="btn btn-sm btn-light" onClick={() => setShowTermsModal(false)}><AutoTranslateText sourceLang="fr">Fermer</AutoTranslateText></button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* ── RGPD MODAL ── */}
      {showRgpdModal && createPortal(
        <div className="ecom-terms-modal-backdrop" onClick={() => setShowRgpdModal(false)}>
          <div className="ecom-terms-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <div className="ecom-terms-modal-header">
              <h5 className="mb-0"><AutoTranslateText sourceLang="fr">RGPD</AutoTranslateText></h5>
              <button type="button" className="btn-close btn-close-white" onClick={() => setShowRgpdModal(false)} style={{ filter: 'invert(1)' }} />
            </div>
            <div className="ecom-terms-modal-body">
              <div style={{ whiteSpace: 'pre-wrap', fontSize: 14, lineHeight: 1.5 }} dangerouslySetInnerHTML={{ __html: (cfg.termsRgpd || '').replace(/\n/g, '<br/>') }} />
            </div>
            <div className="ecom-terms-modal-footer">
              <button type="button" className="btn btn-sm btn-light" onClick={() => setShowRgpdModal(false)}><AutoTranslateText sourceLang="fr">Fermer</AutoTranslateText></button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export const EcommerceShopRenderer = React.memo(EcommerceShopRendererComponent);
